import React, { useState, useEffect, useMemo } from 'react';
import { Button, Card, Row, Col, Badge, Modal, Form, Alert, Spinner, Table, InputGroup, FormControl, Accordion } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCheck, 
  faEye, 
  faArrowLeft,
  faRefresh,
  faExclamationTriangle,
  faTimes,
  faImage,
  faPenToSquare,
  faClipboardList,
  faWarehouse
} from '@fortawesome/free-solid-svg-icons';
import { FullEquipment } from '../interfaces';
import axios from 'axios';
import EquipmentForm from './EquipmentForm';
import EquipmentDetailModal from './EquipmentDetailModal';
import MessageModal from './MessageModal';
import ConfirmationModal from './ConfirmationModal';
import DefectReportGeneralModal from './DefectReportGeneralModal';

import { API_BASE_URL, buildApiUrl } from '../config/api';

/** URL מלא לתמונת דיווח (שרת מחזיר לעיתים נתיב יחסי) */
function resolveDefectImageUrl(path: string | null | undefined): string | null {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  const base = API_BASE_URL.replace(/\/api\/?$/, '');
  return `${base}${path.startsWith('/') ? '' : '/'}${path}`;
}

/** פתיחת תמונת דיווח בכרטיסייה חדשה — endpoint ה-API דורש JWT; window.open לא שולח Authorization */
function formatDefectEditedAt(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('he-IL');
}

async function openDefectImageInNewTab(imageUrl: string) {
  const url = resolveDefectImageUrl(imageUrl) || imageUrl;
  const isDefectApiImage =
    url.includes('/inventory/defect-history/') && url.includes('/image');
  if (isDefectApiImage) {
    try {
      const response = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'image/jpeg',
      });
      const viewUrl = window.URL.createObjectURL(blob);
      window.open(viewUrl, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => window.URL.revokeObjectURL(viewUrl), 60_000);
    } catch (err) {
      console.error('שגיאה בטעינת תמונת דיווח:', err);
      window.open(url, '_blank', 'noopener,noreferrer');
    }
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

interface DefectiveEquipmentManagementProps {
  onBack: () => void;
}

// פונקציה ליצירת רשימת דיווחי תקלות נפרדים
const createDefectInstancesList = (defectHistoryData: any[]) => {
  const defectInstances: any[] = [];
  
  defectHistoryData.forEach(defectRecord => {
    const equipment = defectRecord.equipment_details;
    if (equipment) {
      defectInstances.push({
        id: defectRecord.id,
        defect_id: defectRecord.id,
        equipment_id: defectRecord.equipment,
        equipment_name: equipment.name,
        category_name: equipment.category_name || 'לא מסווג',
        subcategory_name: equipment.subcategory_name || 'לא מסווג',
        quantity: defectRecord.quantity,
        description: defectRecord.description || '',
        priority: defectRecord.priority || 'medium',
        defect_date: defectRecord.defect_date,
        reported_by: defectRecord.reported_by_name,
        image_url: resolveDefectImageUrl(defectRecord.image_url ?? defectRecord.image),
        is_heavy: equipment.is_heavy,
        total_quantity: equipment.quantity,
        available_quantity: equipment.available_quantity || 0,
        // שמירת הפריט המקורי לכל הפונקציות
        original_equipment: equipment,
        original_defect: defectRecord,
        last_edited_at: defectRecord.last_edited_at ?? null,
      });
    }
  });
  
  return defectInstances;
};

// פונקציה לקבץ דיווחי תקלות לפי קטגוריות ותתי קטגוריות, ואז לפי פריט ציוד
const groupDefectInstancesByCategory = (defectInstances: any[]) => {
  const grouped: { [category: string]: { [subcategory: string]: { [equipment: string]: any[] } } } = {};
  defectInstances.forEach(instance => {
    const category = instance.category_name || 'לא מסווג';
    const subcategory = instance.subcategory_name || 'לא מסווג';
    const equipment = instance.equipment_name || 'לא ידוע';
    
    if (!grouped[category]) grouped[category] = {};
    if (!grouped[category][subcategory]) grouped[category][subcategory] = {};
    if (!grouped[category][subcategory][equipment]) grouped[category][subcategory][equipment] = [];
    grouped[category][subcategory][equipment].push(instance);
  });
  return grouped;
};

const DefectiveEquipmentManagement: React.FC<DefectiveEquipmentManagementProps> = ({ onBack }) => {
  const [equipment, setEquipment] = useState<FullEquipment[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [equipmentToEdit, setEquipmentToEdit] = useState<FullEquipment | null>(null);

  const [showMessageModal, setShowMessageModal] = useState<boolean>(false);
  const [messageModalTitle, setMessageModalTitle] = useState<string>('');
  const [messageModalContent, setMessageModalContent] = useState<string>('');
  const [messageModalVariant, setMessageModalVariant] = useState<'success' | 'danger' | 'info' | 'warning'>('info');

  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [confirmModalMessage, setConfirmModalMessage] = useState<string>('');
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
  const [confirmModalTitle, setConfirmModalTitle] = useState<string>('');

  const [showDetailModal, setShowDetailModal] = useState<boolean>(false);
  const [selectedEquipmentForDetail, setSelectedEquipmentForDetail] = useState<FullEquipment | null>(null);
  const [showDefectReportModal, setShowDefectReportModal] = useState<boolean>(false);

  const [showEditDefectModal, setShowEditDefectModal] = useState<boolean>(false);
  const [editingDefectInstance, setEditingDefectInstance] = useState<any | null>(null);
  const [editDefectDescription, setEditDefectDescription] = useState<string>('');
  const [editDefectPriority, setEditDefectPriority] = useState<string>('medium');
  const [editDefectQuantity, setEditDefectQuantity] = useState<number>(1);
  const [savingDefectEdit, setSavingDefectEdit] = useState<boolean>(false);

  const [showViewDefectModal, setShowViewDefectModal] = useState<boolean>(false);
  const [viewingDefectInstance, setViewingDefectInstance] = useState<any | null>(null);

  const showMessage = (title: string, content: string, variant: 'success' | 'danger' | 'info' | 'warning') => {
    setMessageModalTitle(title);
    setMessageModalContent(content);
    setMessageModalVariant(variant);
    setShowMessageModal(true);
  };

  useEffect(() => {
    fetchEquipment();
  }, []);

  const fetchEquipment = async (opts?: { silent?: boolean }) => {
    const silent = opts?.silent === true;
    if (!silent) {
      setLoading(true);
    }
    setError(null);
    try {
      // מביא את רשומות ה-DefectHistory מהשרת
      const defectHistoryResponse = await axios.get(buildApiUrl('inventory/defect-history/'));
      const defectHistoryData = defectHistoryResponse.data;
      
      // מביא את פרטי הציוד
      const equipmentResponse = await axios.get(buildApiUrl('inventory/equipment/'));
      const equipmentData = equipmentResponse.data;
      
      // יוצר מפה של פרטי ציוד לפי ID
      const equipmentMap = new Map();
      equipmentData.forEach((item: FullEquipment) => {
        equipmentMap.set(item.id, item);
      });
      
      // יוצר רשימה של דיווחי תקלות עם פרטי הציוד
      const defectiveEquipmentWithDetails = defectHistoryData.map((defect: any) => {
        const equipment = equipmentMap.get(defect.equipment);
        return {
          ...defect,
          equipment_details: equipment
        };
      });
      
      setEquipment(defectiveEquipmentWithDetails);
    } catch (err) {
      console.error('Error fetching equipment:', err);
      setError('שגיאה בטעינת הציוד');
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const handleMarkAsFixed = (instance: any) => {
    if (!instance.defect_id) {
      showMessage('שגיאה', 'לא ניתן לסמן דיווח תקלה ללא מזהה כתוקן.', 'danger');
      return;
    }
    setConfirmModalTitle('אישור תיקון');
    setConfirmModalMessage(`האם אתה בטוח שברצונך לסמן את התקלה הזו של '${instance.equipment_name}' כתוקנת ולהחזיר ${instance.quantity} יחידות למלאי?`);
    setConfirmAction(() => async () => {
      try {
        // מחיקת רשומת התקלה הספציפית
        await axios.delete(buildApiUrl(`inventory/defect-history/${instance.defect_id}/`));
        
        // קבלת כל התקלות הנותרות של הפריט
        const remainingDefectsResponse = await axios.get(buildApiUrl(`inventory/defect-history/?equipment=${instance.equipment_id}`));
        const remainingDefects = remainingDefectsResponse.data;
        
        // חישוב הכמות התקולה החדשה על בסיס התקלות הנותרות
        const newDefectiveQuantity = remainingDefects.reduce((total: number, defect: any) => total + defect.quantity, 0);
        
        // עדכון הציוד עם הכמות התקולה החדשה
        await axios.patch(buildApiUrl(`inventory/equipment/${instance.equipment_id}/`), {
          defective_quantity: newDefectiveQuantity,
          is_defective: newDefectiveQuantity > 0,
          defect_description: newDefectiveQuantity > 0 ? instance.original_equipment.defect_description : ''
        });
        
        showMessage('הצלחה', `התקלה של '${instance.equipment_name}' סומנה כתוקנת ו-${instance.quantity} יחידות הוחזרו למלאי!`, 'success');
        fetchEquipment();
      } catch (err) {
        console.error('Error marking defect as fixed:', err);
        showMessage('שגיאה', 'שגיאה בסימון התקלה כתוקנת.', 'danger');
      }
    });
    setShowConfirmModal(true);
  };

  const handleMarkAsUnfixable = (instance: any) => {
    if (!instance.defect_id) {
      showMessage('שגיאה', 'לא ניתן לסמן דיווח תקלה ללא מזהה כלא ניתן לתיקון.', 'danger');
      return;
    }
    setConfirmModalTitle('אישור הסרה מהמלאי');
    setConfirmModalMessage(`האם אתה בטוח שברצונך לסמן את התקלה הזו של '${instance.equipment_name}' כלא ניתן לתיקון ולהסיר 1 יחידה מהמלאי הכללי?`);
    setConfirmAction(() => async () => {
      try {
        // מחיקת רשומת התקלה הספציפית
        await axios.delete(buildApiUrl(`inventory/defect-history/${instance.defect_id}/`));
        
        // קבלת כל התקלות הנותרות של הפריט
        const remainingDefectsResponse = await axios.get(buildApiUrl(`inventory/defect-history/?equipment=${instance.equipment_id}`));
        const remainingDefects = remainingDefectsResponse.data;
        
        // חישוב הכמות התקולה החדשה על בסיס התקלות הנותרות
        const newDefectiveQuantity = remainingDefects.reduce((total: number, defect: any) => total + defect.quantity, 0);
        const newTotalQuantity = Math.max(0, instance.original_equipment.quantity - 1);
        
        // עדכון הציוד עם הכמויות החדשות
        await axios.patch(buildApiUrl(`inventory/equipment/${instance.equipment_id}/`), {
          defective_quantity: newDefectiveQuantity,
          quantity: newTotalQuantity,
          is_defective: newDefectiveQuantity > 0,
          defect_description: newDefectiveQuantity > 0 ? instance.original_equipment.defect_description : ''
        });
        
        showMessage('הצלחה', `התקלה של '${instance.equipment_name}' הוסרה ו-1 יחידה הוסרה מהמלאי הכללי!`, 'success');
        fetchEquipment();
      } catch (err) {
        console.error('Error marking defect as unfixable:', err);
        showMessage('שגיאה', 'שגיאה בסימון התקלה כלא ניתן לתיקון.', 'danger');
      }
    });
    setShowConfirmModal(true);
  };

  const handleEditClick = (item: FullEquipment) => {
    setEquipmentToEdit(item);
    setShowForm(true);
  };

  const handleRowClick = (item: FullEquipment) => {
    setSelectedEquipmentForDetail(item);
    setShowDetailModal(true);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEquipmentToEdit(null);
    fetchEquipment();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEquipmentToEdit(null);
  };

  const handleRefresh = () => {
    fetchEquipment();
  };

  const openEditDefectModal = (instance: any) => {
    setEditingDefectInstance(instance);
    setEditDefectDescription(instance.description || '');
    setEditDefectPriority(instance.priority || 'medium');
    setEditDefectQuantity(Math.max(1, instance.quantity || 1));
    setShowEditDefectModal(true);
  };

  const closeEditDefectModal = () => {
    setShowEditDefectModal(false);
    setEditingDefectInstance(null);
    setSavingDefectEdit(false);
  };

  const openViewDefectModal = (instance: any) => {
    setViewingDefectInstance(instance);
    setShowViewDefectModal(true);
  };

  const closeViewDefectModal = () => {
    setShowViewDefectModal(false);
    setViewingDefectInstance(null);
  };

  const handleSaveDefectEdit = async () => {
    if (!editingDefectInstance?.defect_id) {
      showMessage('שגיאה', 'לא ניתן לשמור — חסר מזהה דיווח תקלה.', 'danger');
      return;
    }
    const q = Math.max(1, parseInt(String(editDefectQuantity), 10) || 1);
    setSavingDefectEdit(true);
    try {
      await axios.patch(buildApiUrl(`inventory/defect-history/${editingDefectInstance.defect_id}/`), {
        description: editDefectDescription.trim() || '—',
        priority: editDefectPriority,
        quantity: q,
      });
      showMessage('הצלחה', 'דיווח התקלה עודכן.', 'success');
      closeEditDefectModal();
      await fetchEquipment({ silent: true });
    } catch (err) {
      console.error('Error updating defect:', err);
      showMessage('שגיאה', 'לא ניתן לשמור את עדכון פרטי התקלה.', 'danger');
      setSavingDefectEdit(false);
    }
  };

  const handleDefectReport = async (_defectData: any) => {
    try {
      fetchEquipment();
    } catch (error) {
      console.error('Error submitting defect report:', error);
      showMessage('שגיאה', 'שגיאה בשליחת דיווח התקלות.', 'danger');
    }
  };

  // יצירת רשימת דיווחי תקלות נפרדים וקיבוץ לפי קטגוריות
  const defectInstances = useMemo(() => createDefectInstancesList(equipment), [equipment]);
  const groupedDefectInstances = useMemo(() => groupDefectInstancesByCategory(defectInstances), [defectInstances]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">טוען...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        <Alert.Heading>שגיאה!</Alert.Heading>
        <p>{error}</p>
        <Button variant="outline-danger" onClick={() => fetchEquipment()}>
          <FontAwesomeIcon icon={faRefresh} className="me-1" />
          נסה שוב
        </Button>
      </Alert>
    );
  }

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4 app-sticky-page-toolbar">
        <div className="d-flex align-items-center">
          <Button variant="outline-secondary" onClick={onBack} className="me-3">
            <FontAwesomeIcon icon={faArrowLeft} className="me-1" />
            חזור
          </Button>
          <h2>ניהול ציוד תקול</h2>
        </div>
        <div>
          <Button variant="warning" onClick={() => setShowDefectReportModal(true)} className="me-2">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
            דיווח על תקלות
          </Button>
          <Button variant="outline-primary" onClick={handleRefresh}>
            <FontAwesomeIcon icon={faRefresh} className="me-1" />
            רענן
          </Button>
        </div>
      </div>

      <Card>
        <Card.Header>
          <h5 className="mb-0">ציוד תקול במערכת</h5>
          <small className="text-muted">
            סה"כ {defectInstances.length} דיווחי תקלות
          </small>
        </Card.Header>
        <Card.Body>
          {defectInstances.length === 0 ? (
            <Alert variant="info" className="text-center">
              <Alert.Heading>אין ציוד תקול במערכת</Alert.Heading>
              <p>כל הציוד במערכת תקין וזמין לשימוש.</p>
            </Alert>
          ) : (
            <Accordion defaultActiveKey="0" className="accordion-flush">
              {Object.entries(groupedDefectInstances).map(([category, subcategories], categoryIndex) => (
                <Accordion.Item eventKey={categoryIndex.toString()} key={category}>
                  <Accordion.Header>
                    <div className="d-flex justify-content-between align-items-center w-100 me-3">
                      <span>
                        <strong>{category}</strong>
                        <Badge bg="secondary" className="ms-2">
                          {Object.values(subcategories).flat().length} דיווחי תקלות
                        </Badge>
                      </span>
                    </div>
                  </Accordion.Header>
                  <Accordion.Body>
                    <Accordion defaultActiveKey="0" className="accordion-flush">
                      {Object.entries(subcategories).map(([subcategory, equipmentGroups], subcategoryIndex) => (
                        <Accordion.Item eventKey={`${categoryIndex}-${subcategoryIndex}`} key={`${category}-${subcategory}`}>
                          <Accordion.Header>
                            <div className="d-flex justify-content-between align-items-center w-100 me-3">
                              <span>
                                {subcategory}
                                <Badge bg="info" className="ms-2">
                                  {Object.values(equipmentGroups).flat().length} דיווחי תקלות
                                </Badge>
                              </span>
                            </div>
                          </Accordion.Header>
                          <Accordion.Body>
                            <Accordion defaultActiveKey="0" className="accordion-flush">
                              {Object.entries(equipmentGroups).map(([equipmentName, items], equipmentIndex) => (
                                <Accordion.Item eventKey={`${categoryIndex}-${subcategoryIndex}-${equipmentIndex}`} key={`${category}-${subcategory}-${equipmentName}`}>
                                  <Accordion.Header>
                                    <div className="d-flex justify-content-between align-items-center w-100 me-3">
                                      <span>
                                        <strong>{equipmentName}</strong>
                                        <Badge bg="primary" className="ms-2">
                                          {items.length} תקלות
                                        </Badge>
                                      </span>
                                    </div>
                                  </Accordion.Header>
                                  <Accordion.Body>
                                    <Table striped bordered hover responsive size="sm">
                                      <thead>
                                        <tr>
                                          <th>כמות תקולה</th>
                                          <th>כמות זמינה</th>
                                          <th>תיאור התקלה</th>
                                          <th>
                                            <FontAwesomeIcon icon={faImage} className="me-1" />
                                            תמונה
                                          </th>
                                          <th>פריוריטי</th>
                                          <th>
                                            תאריך דיווח
                                          </th>
                                          <th>תאריך עריכה</th>
                                          <th>דווח על ידי</th>
                                          <th>פעולות</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {items.map(instance => (
                                          <tr key={instance.id}>
                                            <td>
                                              <Badge bg="danger">{instance.quantity}</Badge>
                                            </td>
                                            <td>
                                              <Badge bg="success">{instance.available_quantity}</Badge>
                                            </td>
                                            <td style={{ verticalAlign: 'top', minWidth: '12rem', maxWidth: '36rem' }}>
                                              <div
                                                className="text-break"
                                                style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                                              >
                                                {instance.description || 'אין תיאור'}
                                              </div>
                                            </td>
                                            <td className="text-center align-middle">
                                              {instance.image_url ? (
                                                <Button
                                                  variant="outline-primary"
                                                  size="sm"
                                                  title="פתיחה בכרטיסייה חדשה"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    openDefectImageInNewTab(instance.image_url);
                                                  }}
                                                >
                                                  <FontAwesomeIcon icon={faEye} className="me-1" />
                                                  הצג תמונה
                                                </Button>
                                              ) : (
                                                <span className="text-muted small">—</span>
                                              )}
                                            </td>
                                            <td>
                                              <Badge bg={instance.priority === 'critical' ? 'danger' : 
                                                       instance.priority === 'high' ? 'warning' : 
                                                       instance.priority === 'medium' ? 'info' : 'success'}>
                                                {instance.priority === 'critical' ? 'קריטי' :
                                                 instance.priority === 'high' ? 'גבוה' :
                                                 instance.priority === 'medium' ? 'בינוני' : 'נמוך'}
                                              </Badge>
                                            </td>
                                            <td>
                                              <small>{new Date(instance.defect_date).toLocaleDateString('he-IL')}</small>
                                            </td>
                                            <td>
                                              <small>
                                                {instance.last_edited_at
                                                  ? formatDefectEditedAt(instance.last_edited_at)
                                                  : <span className="text-muted">—</span>}
                                              </small>
                                            </td>
                                            <td>
                                              <small>{instance.reported_by || 'לא ידוע'}</small>
                                            </td>
                                            <td>
                                              <div className="d-flex flex-column gap-2">
                                                <div className="d-flex flex-wrap align-items-center gap-1">
                                                  <small className="text-muted me-1 text-nowrap">דיווח תקלה:</small>
                                                  <Button variant="outline-info" size="sm" title="תצוגה בלבד — פרטי דיווח התקלה" onClick={(e) => { e.stopPropagation(); openViewDefectModal(instance); }}>
                                                    <FontAwesomeIcon icon={faClipboardList} className="me-1" />
                                                    הצגת פרטי התקלה
                                                  </Button>
                                                  <Button variant="outline-primary" size="sm" title="עריכת תיאור, פריוריטי וכמות בדיווח זה" onClick={(e) => { e.stopPropagation(); openEditDefectModal(instance); }}>
                                                    <FontAwesomeIcon icon={faPenToSquare} className="me-1" />
                                                    עריכת דיווח התקלה
                                                  </Button>
                                                </div>
                                                <div className="d-flex flex-wrap align-items-center gap-1">
                                                  <small className="text-muted me-1 text-nowrap">טיפול:</small>
                                                  <Button variant="success" size="sm" onClick={(e) => { e.stopPropagation(); handleMarkAsFixed(instance); }}>
                                                    <FontAwesomeIcon icon={faCheck} className="me-1" />
                                                    תוקן
                                                  </Button>
                                                  <Button variant="danger" size="sm" onClick={(e) => { e.stopPropagation(); handleMarkAsUnfixable(instance); }}>
                                                    <FontAwesomeIcon icon={faTimes} className="me-1" />
                                                    לא ניתן לתיקון
                                                  </Button>
                                                </div>
                                                <div className="d-flex flex-wrap align-items-center gap-1 border-top pt-2 mt-1">
                                                  <small className="text-muted me-1 text-nowrap">פריט במלאי:</small>
                                                  <Button variant="warning" size="sm" title="עריכת כרטיס הציוד במערכת המלאי" onClick={(e) => { e.stopPropagation(); handleEditClick(instance.original_equipment); }}>
                                                    <FontAwesomeIcon icon={faWarehouse} className="me-1" />
                                                    עריכת פריט במלאי
                                                  </Button>
                                                  <Button variant="secondary" size="sm" title="צפייה בפרטי הפריט במלאי" onClick={(e) => { e.stopPropagation(); handleRowClick(instance.original_equipment); }}>
                                                    <FontAwesomeIcon icon={faEye} className="me-1" />
                                                    צפייה בפריט במלאי
                                                  </Button>
                                                </div>
                                              </div>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </Table>
                                  </Accordion.Body>
                                </Accordion.Item>
                              ))}
                            </Accordion>
                          </Accordion.Body>
                        </Accordion.Item>
                      ))}
                    </Accordion>
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
          )}
        </Card.Body>
      </Card>

      {/* Modal לעריכת פריט במלאי (כרטיס ציוד) */}
      <Modal show={showForm} onHide={handleFormCancel} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{equipmentToEdit ? 'עריכת פריט במלאי' : 'הוספת ציוד חדש'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <EquipmentForm
            equipmentToEdit={equipmentToEdit}
            onSuccess={handleFormSuccess}
            onCancel={handleFormCancel}
          />
        </Modal.Body>
      </Modal>

      {/* Modal לצפייה בפריט במלאי */}
      {selectedEquipmentForDetail && (
        <EquipmentDetailModal
          show={showDetailModal}
          onHide={() => setShowDetailModal(false)}
          equipment={selectedEquipmentForDetail}
          allEquipment={equipment}
        />
      )}

      {/* Modal הודעות */}
      <MessageModal
        show={showMessageModal}
        onHide={() => setShowMessageModal(false)}
        title={messageModalTitle}
        message={messageModalContent}
        variant={messageModalVariant}
      />

      {/* Modal אישור */}
      <ConfirmationModal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        onConfirm={() => {
          if (confirmAction) confirmAction();
          setShowConfirmModal(false);
        }}
        title={confirmModalTitle}
        message={confirmModalMessage}
      />

      <DefectReportGeneralModal 
        show={showDefectReportModal} 
        onHide={() => setShowDefectReportModal(false)} 
        onDefectReport={handleDefectReport} 
      />

      <Modal show={showViewDefectModal} onHide={closeViewDefectModal} size="lg" centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-2">
            <FontAwesomeIcon icon={faClipboardList} className="text-info" />
            פרטי דיווח התקלה
            {viewingDefectInstance?.equipment_name ? (
              <span className="text-muted fw-normal fs-6">— {viewingDefectInstance.equipment_name}</span>
            ) : null}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {viewingDefectInstance && (
            <>
              <dl className="row mb-0 small">
                <dt className="col-sm-3">כמות תקולה</dt>
                <dd className="col-sm-9"><Badge bg="danger">{viewingDefectInstance.quantity}</Badge></dd>
                <dt className="col-sm-3">כמות זמינה במלאי</dt>
                <dd className="col-sm-9"><Badge bg="success">{viewingDefectInstance.available_quantity}</Badge></dd>
                <dt className="col-sm-3">פריוריטי</dt>
                <dd className="col-sm-9">
                  <Badge bg={viewingDefectInstance.priority === 'critical' ? 'danger' :
                    viewingDefectInstance.priority === 'high' ? 'warning' :
                    viewingDefectInstance.priority === 'medium' ? 'info' : 'success'}>
                    {viewingDefectInstance.priority === 'critical' ? 'קריטי' :
                     viewingDefectInstance.priority === 'high' ? 'גבוה' :
                     viewingDefectInstance.priority === 'medium' ? 'בינוני' : 'נמוך'}
                  </Badge>
                </dd>
                <dt className="col-sm-3">תאריך דיווח</dt>
                <dd className="col-sm-9">{new Date(viewingDefectInstance.defect_date).toLocaleDateString('he-IL')}</dd>
                <dt className="col-sm-3">תאריך עריכה אחרונה</dt>
                <dd className="col-sm-9">
                  {viewingDefectInstance.last_edited_at
                    ? formatDefectEditedAt(viewingDefectInstance.last_edited_at)
                    : <span className="text-muted">—</span>}
                </dd>
                <dt className="col-sm-3">דווח על ידי</dt>
                <dd className="col-sm-9">{viewingDefectInstance.reported_by || 'לא ידוע'}</dd>
                <dt className="col-sm-3">תיאור התקלה</dt>
                <dd className="col-sm-9">
                  <div className="text-break border rounded p-2 bg-light" style={{ whiteSpace: 'pre-wrap' }}>
                    {viewingDefectInstance.description || 'אין תיאור'}
                  </div>
                </dd>
              </dl>
              {viewingDefectInstance.image_url ? (
                <div className="mt-3">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => openDefectImageInNewTab(viewingDefectInstance.image_url)}
                  >
                    <FontAwesomeIcon icon={faImage} className="me-1" />
                    פתיחת תמונת הדיווח
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeViewDefectModal}>סגור</Button>
          {viewingDefectInstance ? (
            <Button
              variant="primary"
              onClick={() => {
                const inst = viewingDefectInstance;
                closeViewDefectModal();
                if (inst) openEditDefectModal(inst);
              }}
            >
              <FontAwesomeIcon icon={faPenToSquare} className="me-1" />
              עבור לעריכת דיווח
            </Button>
          ) : null}
        </Modal.Footer>
      </Modal>

      <Modal show={showEditDefectModal} onHide={closeEditDefectModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-2">
            <FontAwesomeIcon icon={faPenToSquare} className="text-primary" />
            עריכת דיווח התקלה
            {editingDefectInstance?.equipment_name ? (
              <span className="text-muted fw-normal fs-6">— {editingDefectInstance.equipment_name}</span>
            ) : null}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>תיאור התקלה</Form.Label>
            <Form.Control
              as="textarea"
              rows={6}
              value={editDefectDescription}
              onChange={(e) => setEditDefectDescription(e.target.value)}
              placeholder="תאר את התקלה..."
            />
          </Form.Group>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>פריוריטי</Form.Label>
                <Form.Select
                  value={editDefectPriority}
                  onChange={(e) => setEditDefectPriority(e.target.value)}
                >
                  <option value="low">נמוך</option>
                  <option value="medium">בינוני</option>
                  <option value="high">גבוה</option>
                  <option value="critical">קריטי</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>כמות תקולה</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  value={editDefectQuantity}
                  onChange={(e) => setEditDefectQuantity(Math.max(1, parseInt(e.target.value, 10) || 1))}
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeEditDefectModal} disabled={savingDefectEdit}>
            ביטול
          </Button>
          <Button variant="primary" onClick={handleSaveDefectEdit} disabled={savingDefectEdit}>
            {savingDefectEdit ? 'שומר...' : 'שמור'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DefectiveEquipmentManagement;
